import React, { FC, useState, type PropsWithChildren } from "react";
import AccordionSection from "./AccordionSection";
import { Switch } from "../../../../../components"; // Path from src/pages/dashboard/home/components/link-creator to src/components
import type { ProfileLinkConfig } from "../../../../../client";

// These components are moved here to avoid circular dependencies with LinkCreatorSideMenu.tsx
// For a larger refactor, they might be placed in a shared components directory.
const SettingRow: FC<PropsWithChildren> = ({ children }) => {
  return (
    <div className="flex items-center justify-between gap-2">{children}</div>
  );
};

const SettingText: FC<PropsWithChildren> = ({ children }) => {
  return <p className="text-gray-9 text-sm font-medium grow">{children}</p>;
};

interface ProfileImageLinkSettingsProps {
  disabled?: boolean;
  linkConfig?: ProfileLinkConfig;
  onUpdate: (payload: Partial<ProfileLinkConfig>) => void;
}

const ProfileImageLinkSettings: FC<ProfileImageLinkSettingsProps> = ({
  disabled,
  linkConfig,
  onUpdate,
}) => {
  const [isProfileImageSectionExpanded, setIsProfileImageSectionExpanded] =
    useState(false);

  return (
    <AccordionSection
      title="Profile image"
      subtitle={(() => {
        const activeSettings: string[] = [];
        if (linkConfig?.useProfilePicture) {
          activeSettings.push("Show picture");
          if (linkConfig?.profileLinkPersonalInfoConfig?.roundMode) {
            activeSettings.push("rounded");
          } else {
            activeSettings.push("square");
          }
          if (linkConfig?.profileLinkPersonalInfoConfig?.useBorder) {
            activeSettings.push("border");
          } else {
            activeSettings.push("no border");
          }
        }
        if (activeSettings.length === 0) {
          return "Picture hidden";
        }
        return activeSettings.join(", ");
      })()}
      expanded={isProfileImageSectionExpanded}
      onToggle={() =>
        setIsProfileImageSectionExpanded(!isProfileImageSectionExpanded)
      }
    >
      <SettingRow>
        <Switch
          checked={linkConfig?.useProfilePicture}
          onCheckedChange={(checked) => {
            if (typeof checked !== "boolean") return;

            onUpdate({
              useProfilePicture: checked,
            });
          }}
          disabled={disabled}
        />
        <SettingText>Display picture</SettingText>
      </SettingRow>
      <SettingRow>
        <Switch
          checked={linkConfig?.profileLinkPersonalInfoConfig?.roundMode}
          onCheckedChange={(checked) => {
            if (typeof checked !== "boolean") return;

            onUpdate({
              profileLinkPersonalInfoConfig: {
                ...linkConfig?.profileLinkPersonalInfoConfig,
                roundMode: checked,
              },
            });
          }}
          disabled={disabled || !linkConfig?.useProfilePicture}
        />
        <SettingText>Round picture</SettingText>
      </SettingRow>
      <SettingRow>
        <Switch
          checked={linkConfig?.profileLinkPersonalInfoConfig?.useBorder}
          onCheckedChange={(checked) => {
            if (typeof checked !== "boolean") return;

            onUpdate({
              profileLinkPersonalInfoConfig: {
                ...linkConfig?.profileLinkPersonalInfoConfig,
                useBorder: checked,
              },
            });
          }}
          disabled={disabled || !linkConfig?.useProfilePicture}
        />
        <SettingText>Use border</SettingText>
      </SettingRow>
    </AccordionSection>
  );
};

export default ProfileImageLinkSettings;
